/**
 * BlogSearch – Globale Blog-Suchkomponente
 * Schwebt als Lupe-Icon über allen Seiten (z-60).
 * Klick → Suchfeld expandiert, Live-Suche durch Blog-Artikel.
 * Eigenständig, keine Abhängigkeit zu bestehenden Nav-Komponenten.
 */
import { lazy, Suspense, useState, useRef, useEffect } from "react";
import { Search, X } from "lucide-react";
import { useLocation } from "wouter";

const BlogSearchResults = lazy(() => import("./BlogSearchResults"));

export default function BlogSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [, navigate] = useLocation();

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) {
      document.addEventListener("keydown", handleEsc);
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open]);

  const handleSelect = (slug: string) => {
    setOpen(false);
    setQuery("");
    navigate(`/blog/${slug}`);
  };

  return (
    <div ref={containerRef} className="immozwerg-blog-search fixed top-2.5 right-[4.5rem] z-[90] pointer-events-auto lg:top-5 lg:right-[17rem]">
      {/* Lupe-Button (immer sichtbar) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="w-11 h-11 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm shadow-md border border-[#e6dcd2] hover:border-[#a066c2] hover:shadow-lg transition-all duration-200"
          aria-label="Blog durchsuchen"
        >
          <Search size={16} className="text-[#631172]" />
        </button>
      )}

      {/* Expandiertes Suchfeld */}
      {open && (
        <div className="flex flex-col">
          {/* Suchfeld */}
          <div className="flex items-center gap-2 bg-white rounded-full shadow-lg border border-[#e6dcd2] px-3 py-2 w-[280px] sm:w-[340px] transition-all duration-200">
            <Search size={15} className="text-[#631172] shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Suchwort eingeben…"
              className="flex-1 text-sm text-[#2d1b4e] bg-transparent outline-none placeholder:text-gray-400"
            />
            <button
              onClick={() => { setOpen(false); setQuery(""); }}
              className="w-11 h-11 inline-flex items-center justify-center text-gray-400 hover:text-[#631172] transition-colors shrink-0"
              aria-label="Suche schließen"
            >
              <X size={16} />
            </button>
          </div>

          {/* Blogdaten und Ergebnisliste werden erst nach Öffnen der Suche geladen. */}
          <Suspense fallback={query.trim() ? <div className="mt-2 bg-white rounded-xl shadow-xl border border-[#e6dcd2] px-4 py-6 text-center text-sm text-gray-400">Suche wird geladen…</div> : null}>
            <BlogSearchResults query={query} onSelect={handleSelect} />
          </Suspense>
        </div>
      )}
    </div>
  );
}
